import React from 'react'
// import './create-journey.scss'
import { PageHeader } from '@pushly/aqe/lib/components'
import { BetterComponent } from '../../../components/better-component/better-component'
import { AppState } from '../../../stores/app'
import { AppService, JourneyService, NotificationService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import { JourneyBuilderMode } from '../../../components/journey-builder/enums'
import { ifDomainChanged } from '../../../_utils/domain'
import { JourneyWizard } from '../../../components/journey-wizard/journey-wizard'
import { Journey } from '../types/journey-api-response'
import { reassignStepIds } from '../../../_utils/journeys'
import { ApiVersion } from '../../../enums/api-version.enum'
import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import { JourneyStatusRef } from '../types/journey-nodes'
import { StatusType } from '../../../enums/status-type'
import { isActionStep } from '@pushly/models/lib/structs/journeys'
import { simpleNotification } from '@pushly/aqe/lib/utils'

interface ICreateJourney {}

interface IState {
    loading: boolean
    journey?: Journey
}

export class CreateJourney extends BetterComponent<ICreateJourney, IState> {
    private appState: AppState
    private appService: AppService
    private journeyService: JourneyService
    private notifService: NotificationService

    private disposeObservers: (() => void)[] = []
    private wizardRef = React.createRef<JourneyStatusRef>()

    public constructor(props: ICreateJourney) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.journeyService = Container.get(JourneyService)
        this.notifService = Container.get(NotificationService)

        this.state = {
            loading: true,
        }
    }

    public componentDidMount(): void {
        this.loadRequestedTemplate()

        this.disposeObservers.push(
            ifDomainChanged(this.appState, () => this.appService.routeWithinDomain('/journeys')),
        )
    }

    public componentWillUnmount() {
        super.componentWillUnmount()

        this.disposeObservers.forEach((disposeFn) => disposeFn())
    }

    public render(): React.ReactNode {
        return (
            <div className="create-journey">
                <PageHeader title="Create Journey" onTitleSet={this.appService.customizeTabTitle} />

                <JourneyWizard
                    wizardRef={this.wizardRef}
                    loading={this.state.loading}
                    mode={JourneyBuilderMode.CREATE}
                    domain={this.appState.currentDomain}
                    journey={this.state.journey}
                    onChange={this.handleUpdate}
                    tabsProps={{
                        destroyInactiveTabPane: true,
                    }}
                    canTabNavigate={() => {
                        if (this.wizardRef.current?.builderHasChanged) {
                            simpleNotification('error', 'You have unsaved changes. Please save before continuing.')
                            return false
                        }

                        return true
                    }}
                />
            </div>
        )
    }

    protected get templateId(): number | undefined {
        const raw = this.queryString.template
        if (typeof raw !== 'string') return undefined
        const parsed = Number.parseInt(raw, 10)
        return Number.isNaN(parsed) ? undefined : parsed
    }

    protected handleUpdate = (journey: Journey) => {
        this.setState({ journey })
    }

    protected loadRequestedTemplate = async (): Promise<void> => {
        const update: Partial<IState> = { loading: false }

        if (this.templateId != null) {
            const { ok, data } = await this.journeyService.fetchById(this.templateId)
            const template = data as Journey

            if (ok) {
                update.journey = {
                    ...template,
                    id: 0,
                    status_id: StatusType.DRAFT.id,
                    status: StatusType.DRAFT.name,
                    // A duplicate is a new draft: it publishes Active unless the person chooses otherwise.
                    configuration: { ...template.configuration, status_on_publish: undefined },
                } as Journey

                let steps = template.revision?.steps ?? []
                if (Array.isArray(steps) && steps.length > 0) {
                    steps = reassignStepIds(steps)

                    const actionSteps = steps.filter(isActionStep).filter(
                        (s) => !!s.configuration.params?.notification_id,
                    )
                    if (actionSteps.length > 0) {
                        await Promise.all(
                            actionSteps.map((s) => {
                                const notificationId = s.configuration.params.notification_id
                                if (notificationId == null) return Promise.resolve()

                                return this.notifService
                                        .fetchNotificationById(this.appState.currentDomain!.id, notificationId, {
                                            query: { include_schedules: false },
                                            version: ApiVersion.V4,
                                        })
                                        .then((res) => {
                                            if (this.templateId != null) {
                                                delete s.configuration.params.notification_id
                                            }

                                            if (!!res?.template?.channels?.default) {
                                                // remove previous default actions if override flag is false
                                                // and persisted default actions are returned
                                                //
                                                const useDefaultActions =
                                                    (res?.template.channels.web?.display_meta
                                                        ?.override_default_actions === false ||
                                                        res?.template.channels.native_android?.display_meta
                                                            ?.override_default_actions === false) ??
                                                    false
                                                const hasActions =
                                                    (res.template.channels.default.actions?.length ?? 0) > 0
                                                if (hasActions) {
                                                    if (useDefaultActions) {
                                                        delete res.template.channels.default.actions
                                                    } else {
                                                        res.template.channels.default.actions?.map((a) => delete a.id)

                                                        res.channels.forEach((channel) => {
                                                            const chKeyFormatted = channel.toLowerCase()

                                                            // strip actions when defaults should be applied for action enabled templates
                                                            if (
                                                                channel === DeliveryChannel.WEB ||
                                                                channel === DeliveryChannel.NATIVE_ANDROID
                                                            ) {
                                                                const actions =
                                                                    res.template.channels[chKeyFormatted]?.actions
                                                                if (actions?.length > 0) {
                                                                    actions.map((a) => delete a.id)
                                                                }
                                                            }
                                                        })
                                                    }
                                                }
                                            }

                                            s.configuration.params.notification = res
                                        })
                            }),
                        )
                    }
                }

                if (update.journey) {
                    update.journey.revision = {
                        id: 0,
                        is_active: false,
                        created_at: new Date().toISOString(),
                        steps,
                    }
                }
            }
        }

        this.setState((prev) => ({ ...prev, ...update }))
    }
}

